import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button, Icon } from '@picsio/ui';
import { TriangleArrowDown } from '@picsio/icons';
import cn from 'classnames';

import localization from '../../../../shared/strings';
import Logger from '../../../../services/Logger';
import DialogIcon from '../../../Icon';
import RadioItem from '../../../RadioItem';
import ChannelTabsHeader from '../MediaSelection/ChannelTabsHeader';
import { toggleExpandedVariantIds } from '../helpers/variantMediaAssignmentHelpers';
import ButtonLoader from '../ButtonLoader';
import styles from '../styles.module.scss';

const EMPTY_ALT_VALUE = '__empty__';

const getOptionValue = (altText) => (altText === '' ? EMPTY_ALT_VALUE : altText);

const parseOptionValue = (value) => (value === EMPTY_ALT_VALUE ? '' : value);

const formatAltOptionLabel = (option, strings) => {
  if (option.altText === '') {
    return strings.NO_ALT_TEXT;
  }

  return `"${option.altText}"`;
};

const formatSourceLabel = (option, strings) => {
  if (option.sourceType === 'product') {
    return `${strings.SOURCE_PREFIX} ${strings.SOURCE_PRODUCT}`;
  }

  const sourceLabel = option.sourceLabels?.[0];
  if (!sourceLabel) {
    return '';
  }

  return `${strings.SOURCE_PREFIX} ${sourceLabel}`;
};

const getAssetFilename = (asset) => (
  asset?.name || asset?.title || asset?.fileName || 'Untitled'
);

const getAssetThumbnailUrl = (asset) => (
  asset?.thumbnail?.small || asset?.thumbnail?.medium || asset?.url
);

const getSelectedSourceLabel = (conflict, selectedAltText, strings) => {
  if (selectedAltText === '') {
    const emptyOption = conflict.options.find((option) => option.altText === '');
    if (emptyOption) {
      return formatSourceLabel(emptyOption, strings);
    }
    return '';
  }

  const selectedOption = conflict.options.find((option) => option.altText === selectedAltText);
  return selectedOption ? formatSourceLabel(selectedOption, strings) : '';
};

function AltConflictAssetSection({
  conflict,
  altStrings,
  isExpanded,
  selectedValue,
  onToggleExpand,
  onResolutionChange,
}) {
  const thumbnailUrl = getAssetThumbnailUrl(conflict.asset);
  const filename = getAssetFilename(conflict.asset);
  const headerSource = getSelectedSourceLabel(conflict, parseOptionValue(selectedValue), altStrings);

  return (
    <div className={styles.connectChannelSectionAltConflictSection}>
      <button
        type="button"
        className={styles.connectChannelSectionAltConflictHeader}
        onClick={onToggleExpand}
      >
        <Icon
          size="lg"
          className={cn(styles.connectChannelSectionAltConflictChevron, {
            [styles.connectChannelSectionAltConflictChevronExpanded]: isExpanded,
          })}
        >
          <TriangleArrowDown />
        </Icon>
        <span className={styles.connectChannelSectionAltConflictFilename}>{filename}</span>
        <If condition={headerSource}>
          <span className={styles.connectChannelSectionAltConflictHeaderSource}>{headerSource}</span>
        </If>
      </button>

      <If condition={isExpanded}>
        <div className={styles.connectChannelSectionAltConflictBody}>
          <If condition={thumbnailUrl}>
            <img
              src={thumbnailUrl}
              alt=""
              className={styles.connectChannelSectionAltConflictThumb}
            />
          </If>
          <div className={styles.connectChannelSectionAltConflictOptions}>
            {conflict.options.map((option) => {
              const optionValue = getOptionValue(option.altText);
              const isSelected = selectedValue === optionValue;

              return (
                <div
                  key={`${conflict.assetId}:${optionValue}`}
                  className={styles.connectChannelSectionAltConflictOptionRow}
                >
                  <div className={cn(styles.connectChannelSectionAltConflictOptionRadio, {
                    [styles.connectChannelSectionAltConflictOptionRadioMuted]: !isSelected,
                  })}
                  >
                    <RadioItem
                      value={optionValue}
                      checkedValue={selectedValue}
                      label={formatAltOptionLabel(option, altStrings)}
                      onChange={(value) => onResolutionChange(value)}
                    />
                  </div>
                  <span className={styles.connectChannelSectionAltConflictOptionSource}>
                    {formatSourceLabel(option, altStrings)}
                  </span>
                </div>
              );
            })}
            <If condition={!conflict.options.some((option) => option.altText === '')}>
              <div className={styles.connectChannelSectionAltConflictOptionRow}>
                <div className={cn(styles.connectChannelSectionAltConflictOptionRadio, {
                  [styles.connectChannelSectionAltConflictOptionRadioMuted]:
                    selectedValue !== EMPTY_ALT_VALUE,
                })}
                >
                  <RadioItem
                    value={EMPTY_ALT_VALUE}
                    checkedValue={selectedValue}
                    label={altStrings.NO_ALT_TEXT}
                    onChange={(value) => onResolutionChange(value)}
                  />
                </div>
              </div>
            </If>
          </div>
        </div>
      </If>

      <div className={styles.connectChannelSectionAltConflictDivider} />
    </div>
  );
}

AltConflictAssetSection.propTypes = {
  conflict: PropTypes.shape({
    assetId: PropTypes.string.isRequired,
    asset: PropTypes.shape({}),
    options: PropTypes.arrayOf(PropTypes.shape({
      altText: PropTypes.string,
      sourceLabels: PropTypes.arrayOf(PropTypes.string),
      sourceType: PropTypes.string,
    })),
  }).isRequired,
  altStrings: PropTypes.shape({}).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  selectedValue: PropTypes.string.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onResolutionChange: PropTypes.func.isRequired,
};

function MediaAltConflictDialog({
  channelOptions,
  selectedChannels,
  conflictsByChannel,
  validateBeforeFinish,
  onConfirm,
  onBack,
  onCancel,
  onValidationAbort,
  root,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;
  const altStrings = strings.ALT_CONFLICTS;

  const channels = useMemo(
    () => channelOptions.filter(({ value }) => selectedChannels.includes(value)),
    [channelOptions, selectedChannels],
  );

  const conflictChannels = useMemo(
    () => channels.filter(({ value }) => (conflictsByChannel[value] || []).length > 0),
    [channels, conflictsByChannel],
  );

  const [activeChannelId, setActiveChannelId] = useState(conflictChannels[0]?.value);
  const [resolutionsByChannel, setResolutionsByChannel] = useState(() => (
    Object.entries(conflictsByChannel).reduce((acc, [channelId, conflicts]) => {
      acc[channelId] = conflicts.reduce((channelResolutions, conflict) => {
        const firstOption = conflict.options[0];
        channelResolutions[conflict.assetId] = firstOption?.altText ?? '';
        return channelResolutions;
      }, {});
      return acc;
    }, {})
  ));
  const [expandedAssetIds, setExpandedAssetIds] = useState(() => {
    const firstConflict = conflictsByChannel[conflictChannels[0]?.value]?.[0];
    return new Set(firstConflict ? [firstConflict.assetId] : []);
  });
  const [isConfirming, setIsConfirming] = useState(false);

  const activeChannel = conflictChannels.find(({ value }) => value === activeChannelId)
    || conflictChannels[0];
  const activeConflicts = conflictsByChannel[activeChannel?.value] || [];
  const isMultiChannel = conflictChannels.length > 1;
  const activeResolutions = resolutionsByChannel[activeChannel?.value] || {};

  useEffect(() => {
    const conflicts = conflictsByChannel[activeChannel?.value] || [];
    const firstAssetId = conflicts[0]?.assetId;
    setExpandedAssetIds(firstAssetId ? new Set([firstAssetId]) : new Set());
  }, [activeChannel?.value, conflictsByChannel]);

  const isComplete = useMemo(
    () => conflictChannels.every(({ value: channelId }) => {
      const conflicts = conflictsByChannel[channelId] || [];
      const channelResolutions = resolutionsByChannel[channelId] || {};

      return conflicts.every((conflict) => (
        channelResolutions[conflict.assetId] !== undefined
        && channelResolutions[conflict.assetId] !== null
      ));
    }),
    [conflictChannels, conflictsByChannel, resolutionsByChannel],
  );

  const isDestroyedRef = useRef(false);

  const destroy = useCallback(() => {
    if (isDestroyedRef.current) {
      return;
    }

    isDestroyedRef.current = true;

    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handlePrevious = useCallback(() => {
    destroy();
    onBack?.(resolutionsByChannel);
  }, [destroy, onBack, resolutionsByChannel]);

  const handleResolutionChange = useCallback((assetId, value) => {
    const channelId = activeChannel?.value;
    if (!channelId) return;

    setResolutionsByChannel((prev) => ({
      ...prev,
      [channelId]: {
        ...(prev[channelId] || {}),
        [assetId]: parseOptionValue(value),
      },
    }));
  }, [activeChannel?.value]);

  const handleToggleExpand = useCallback((assetId) => {
    setExpandedAssetIds((prev) => toggleExpandedVariantIds(prev, assetId));
  }, []);

  const getBadgeCount = useCallback(
    (channelId) => (conflictsByChannel[channelId] || []).length,
    [conflictsByChannel],
  );

  const logConfirmed = useCallback(() => {
    const conflictCount = Object.values(conflictsByChannel)
      .reduce((total, conflicts) => total + (conflicts?.length || 0), 0);

    Logger.log('User', 'ProductsSyncAltConflictsConfirmed', {
      channelCount: conflictChannels.length,
      conflictCount,
    });
  }, [conflictChannels.length, conflictsByChannel]);

  const handleConfirm = useCallback(async () => {
    if (!isComplete || isConfirming) return;

    if (validateBeforeFinish) {
      setIsConfirming(true);

      try {
        await validateBeforeFinish(() => {
          setIsConfirming(false);
          destroy();
        });
        logConfirmed();
        destroy();
        onConfirm(resolutionsByChannel);
      } catch {
        if (isDestroyedRef.current) {
          onValidationAbort?.();
          return;
        }

        setIsConfirming(false);
      }

      return;
    }

    logConfirmed();
    destroy();
    onConfirm(resolutionsByChannel);
  }, [
    destroy,
    isComplete,
    isConfirming,
    logConfirmed,
    onConfirm,
    onValidationAbort,
    resolutionsByChannel,
    validateBeforeFinish,
  ]);

  return (
    <div className={cn(
      'simpleDialog',
      styles.connectChannelSectionModal,
      styles.connectChannelSectionModalWide,
      styles.connectChannelSectionDialogWithSubheader,
    )}
    >
      <div className="simpleDialogUnderlayer" />
      <div className={cn('simpleDialogBox', styles.connectChannelSectionModalBoxWide)}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{altStrings.TITLE}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <DialogIcon name="close" />
          </span>
        </div>
        <div className="simpleDialogSubHeader">
          <p>{altStrings.DESCRIPTION}</p>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <If condition={isMultiChannel}>
              <ChannelTabsHeader
                channels={conflictChannels}
                activeChannelId={activeChannel?.value}
                getBadgeCount={getBadgeCount}
                onChange={setActiveChannelId}
              />
            </If>
            <div className={styles.connectChannelSectionAltConflicts}>
              {activeConflicts.map((conflict) => (
                <AltConflictAssetSection
                  key={conflict.assetId}
                  conflict={conflict}
                  altStrings={altStrings}
                  isExpanded={expandedAssetIds.has(conflict.assetId)}
                  selectedValue={getOptionValue(activeResolutions[conflict.assetId] ?? '')}
                  onToggleExpand={() => handleToggleExpand(conflict.assetId)}
                  onResolutionChange={(value) => handleResolutionChange(conflict.assetId, value)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={cn('simpleDialogFooter', styles.connectChannelSectionAltConflictFooter)}>
          <Button
            color="secondary"
            size="md"
            variant="contained"
            disabled={isConfirming}
            onClick={handlePrevious}
          >
            {altStrings.BTN_BACK}
          </Button>
          <Button
            color="primary"
            size="md"
            variant="contained"
            disabled={!isComplete || isConfirming}
            title={!isComplete ? altStrings.INCOMPLETE_TOOLTIP : undefined}
            endIcon={isConfirming ? <ButtonLoader /> : null}
            onClick={handleConfirm}
          >
            {altStrings.BTN_CONFIRM}
          </Button>
        </div>
      </div>
    </div>
  );
}

MediaAltConflictDialog.propTypes = {
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  selectedChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
  conflictsByChannel: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    assetId: PropTypes.string.isRequired,
    asset: PropTypes.shape({}),
    options: PropTypes.arrayOf(PropTypes.shape({
      altText: PropTypes.string,
      sourceLabels: PropTypes.arrayOf(PropTypes.string),
      sourceType: PropTypes.string,
    })),
  }))).isRequired,
  validateBeforeFinish: PropTypes.func,
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  onValidationAbort: PropTypes.func,
};

MediaAltConflictDialog.defaultProps = {
  validateBeforeFinish: undefined,
  onBack: undefined,
  onCancel: undefined,
  onValidationAbort: undefined,
};

export function showMediaAltConflictDialog(props) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <MediaAltConflictDialog
      {...props}
      root={root}
    />,
  );
}

export default MediaAltConflictDialog;
